import React from 'react';
import {
  // Analysis & Research Icons
  BarChart3,
  TrendingUp,
  Search,
  Microscope,
  Users,
  Bot,
  // Emergency & Response Icons
  AlertTriangle,
  AlertCircle,
  Siren,
  Activity,
  Zap,
  // Forms & Documentation Icons
  FileText,
  ClipboardList,
  FileCheck,
  Edit3,
  MessageCircle,
  // Field Response Icons
  MapPin,
  Navigation,
  Users2,
  Package,
  Truck,
  Wrench,
  Handshake,
  Hospital,
  // Strategic Icons
  Target,
  Settings,
  CheckCircle,
  // Workspace Icons
  Briefcase,
  Monitor,
  Database,
  Globe,
  // Additional Icons
  ArrowRight,
  Plus,
  X,
  Menu,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  // Missing Icons
  Clock,
  Shield,
  Heart
} from 'lucide-react';

export interface SwissIconProps {
  size?: number | string;
  className?: string;
  color?: string;
  strokeWidth?: number;
}

// Swiss Icon Component Base
const SwissIcon: React.FC<SwissIconProps & { icon: React.ComponentType<any> }> = ({
  icon: Icon,
  size = 20,
  className = '',
  color = 'currentColor',
  strokeWidth = 1.5,
  ...props
}) => {
  return (
    <Icon
      size={size}
      color={color}
      strokeWidth={strokeWidth}
      className={`swiss-icon ${className}`}
      {...props}
    />
  );
};

// Swiss Design Icon Library
export const SwissIcons = {
  // Analysis & Research (replaces 📊, 🔬, 📈, 🤖)
  Analytics: (props: SwissIconProps) => <SwissIcon icon={BarChart3} {...props} />,
  Research: (props: SwissIconProps) => <SwissIcon icon={Microscope} {...props} />,
  TrendAnalysis: (props: SwissIconProps) => <SwissIcon icon={TrendingUp} {...props} />,
  AISystem: (props: SwissIconProps) => <SwissIcon icon={Bot} {...props} />,
  Search: (props: SwissIconProps) => <SwissIcon icon={Search} {...props} />,
  
  // Emergency & Alerts (replaces 🚨, ⚠️, 🔥)
  Emergency: (props: SwissIconProps) => <SwissIcon icon={Siren} {...props} />,
  Warning: (props: SwissIconProps) => <SwissIcon icon={AlertTriangle} {...props} />,
  Alert: (props: SwissIconProps) => <SwissIcon icon={AlertCircle} {...props} />,
  Critical: (props: SwissIconProps) => <SwissIcon icon={Activity} {...props} />,
  Urgent: (props: SwissIconProps) => <SwissIcon icon={Zap} {...props} />,
  
  // People & Teams (replaces 👥, 👨‍🔬, 🏃)
  Users: (props: SwissIconProps) => <SwissIcon icon={Users} {...props} />,
  Team: (props: SwissIconProps) => <SwissIcon icon={Users2} {...props} />,
  FieldWorker: (props: SwissIconProps) => <SwissIcon icon={Navigation} {...props} />,
  Displacement: (props: SwissIconProps) => <SwissIcon icon={Navigation} {...props} />,
  
  // Documentation & Forms (replaces 📝, 📋, ✅)
  Document: (props: SwissIconProps) => <SwissIcon icon={FileText} {...props} />,
  Form: (props: SwissIconProps) => <SwissIcon icon={ClipboardList} {...props} />,
  Checklist: (props: SwissIconProps) => <SwissIcon icon={FileCheck} {...props} />,
  Edit: (props: SwissIconProps) => <SwissIcon icon={Edit3} {...props} />,
  Approved: (props: SwissIconProps) => <SwissIcon icon={CheckCircle} {...props} />,
  
  // Communication (replaces 💬)
  Message: (props: SwissIconProps) => <SwissIcon icon={MessageCircle} {...props} />,
  
  // Operations & Logistics (replaces 📦, 🚚, 🛠️, 🤝)
  Supplies: (props: SwissIconProps) => <SwissIcon icon={Package} {...props} />,
  Distribution: (props: SwissIconProps) => <SwissIcon icon={Truck} {...props} />,
  Tools: (props: SwissIconProps) => <SwissIcon icon={Wrench} {...props} />,
  Coordination: (props: SwissIconProps) => <SwissIcon icon={Handshake} {...props} />,
  
  // Medical & Health (replaces 🏥)
  Medical: (props: SwissIconProps) => <SwissIcon icon={Hospital} {...props} />,
  
  // Strategic & Management (replaces 🎯)
  Strategic: (props: SwissIconProps) => <SwissIcon icon={Target} {...props} />,
  Settings: (props: SwissIconProps) => <SwissIcon icon={Settings} {...props} />,
  
  // System & Platform (replaces 🌐, 💻)
  Global: (props: SwissIconProps) => <SwissIcon icon={Globe} {...props} />,
  Globe: (props: SwissIconProps) => <SwissIcon icon={Globe} {...props} />,
  System: (props: SwissIconProps) => <SwissIcon icon={Monitor} {...props} />,
  Database: (props: SwissIconProps) => <SwissIcon icon={Database} {...props} />,
  Workspace: (props: SwissIconProps) => <SwissIcon icon={Briefcase} {...props} />,
  
  // Time & Protection Icons
  Clock: (props: SwissIconProps) => <SwissIcon icon={Clock} {...props} />,
  Protection: (props: SwissIconProps) => <SwissIcon icon={Shield} {...props} />,
  Health: (props: SwissIconProps) => <SwissIcon icon={Heart} {...props} />,
  
  // Logistics Aliases
  Package: (props: SwissIconProps) => <SwissIcon icon={Package} {...props} />,
  
  // Navigation & UI
  ArrowRight: (props: SwissIconProps) => <SwissIcon icon={ArrowRight} {...props} />,
  Plus: (props: SwissIconProps) => <SwissIcon icon={Plus} {...props} />,
  Close: (props: SwissIconProps) => <SwissIcon icon={X} {...props} />,
  Menu: (props: SwissIconProps) => <SwissIcon icon={Menu} {...props} />,
  ChevronDown: (props: SwissIconProps) => <SwissIcon icon={ChevronDown} {...props} />,
  ChevronUp: (props: SwissIconProps) => <SwissIcon icon={ChevronUp} {...props} />,
  ChevronLeft: (props: SwissIconProps) => <SwissIcon icon={ChevronLeft} {...props} />,
  ChevronRight: (props: SwissIconProps) => <SwissIcon icon={ChevronRight} {...props} />,
};

// Emoji to Swiss Icon Mapping
export const emojiToSwissIcon: Record<string, keyof typeof SwissIcons> = {
  // Analysis & Research
  '📊': 'Analytics',
  '🔬': 'Research', 
  '📈': 'TrendAnalysis',
  '🤖': 'AISystem',
  '🔍': 'Search',
  
  // Emergency & Alerts
  '🚨': 'Emergency',
  '⚠️': 'Warning',
  '🔥': 'Critical',
  '⚡': 'Urgent',
  
  // People & Teams
  '👥': 'Users',
  '👨‍🔬': 'FieldWorker',
  '🏃': 'Displacement',
  '🏃‍♂️': 'Displacement',
  
  // Documentation & Forms
  '📝': 'Document',
  '📋': 'Form',
  '✅': 'Approved',
  
  // Communication
  '💬': 'Message',
  
  // Operations & Logistics
  '📦': 'Supplies',
  '🚚': 'Distribution',
  '🛠️': 'Tools',
  '🤝': 'Coordination',
  
  // Medical & Health
  '🏥': 'Medical',
  '😱': 'Emergency', // Panic alert maps to emergency
  
  // Strategic & Management
  '🎯': 'Strategic',
  
  // System & Platform
  '🌐': 'Global',
  '💻': 'System',
};

// Helper function to get Swiss icon from emoji
export const getSwissIconFromEmoji = (emoji: string): keyof typeof SwissIcons | null => {
  return emojiToSwissIcon[emoji] || null;
};

// Swiss Icon with Label Component
export interface SwissIconWithLabelProps extends SwissIconProps {
  icon: keyof typeof SwissIcons;
  label: string;
  layout?: 'horizontal' | 'vertical';
  labelClassName?: string;
}

export const SwissIconWithLabel: React.FC<SwissIconWithLabelProps> = ({
  icon,
  label,
  layout = 'horizontal',
  labelClassName = '',
  className = '',
  ...iconProps
}) => {
  const IconComponent = SwissIcons[icon];
  
  if (layout === 'vertical') {
    return (
      <div className={`swiss-icon-label-vertical flex flex-col items-center gap-1 ${className}`}>
        <IconComponent {...iconProps} />
        <span className={`text-xs font-medium ${labelClassName}`}>{label}</span>
      </div>
    );
  }
  
  return (
    <div className={`swiss-icon-label-horizontal flex items-center gap-2 ${className}`}>
      <IconComponent {...iconProps} />
      <span className={`font-medium ${labelClassName}`}>{label}</span>
    </div>
  );
};

// Export default for convenience
export default SwissIcons;